import React from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSearch, FiFilter, FiInfo, FiStar, FiDatabase } = FiIcons

const LinkDirectoryFilters = ({ filters, onFilterChange, uniqueNiches, uniqueTypes, uniqueStates, totalOpportunities, onExportCSV }) => {
  const handleFilterChange = (key, value) => {
    onFilterChange({ ...filters, [key]: value })
  }

  const clearFilters = () => {
    onFilterChange({ niche: '', type: '', state: '', search: '', favorites: false })
  }

  const hasActiveFilters = filters.niche || filters.type || filters.state || filters.search || filters.favorites

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-lg p-6 mb-8 dark:bg-gray-800"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <div className="bg-[#D59A4C20] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiFilter} className="w-5 h-5 text-[#D59A4C]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filter Opportunities</h3>
        </div>
        <div className="flex items-center space-x-3">
          {/* Opportunity Count */}
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
            Showing {totalOpportunities} Opportunities
          </div>
          <b><button
                onClick={onExportCSV}
                className="flex items-center text-sm text-[#D59A4C] hover:text-[#B58C54] no-border"
              >
                <SafeIcon icon={FiDatabase} className="w-4 h-4 mr-2" />
                Export CSV
              </button></b>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 px-1 py-1 no-border"
            >
             ✕ Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Info message */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg dark:bg-blue-900/30">
        <div className="flex items-start">
          <SafeIcon
            icon={FiInfo}
            className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0"
          />
          <p className="text-sm text-blue-800 dark:text-blue-300">
            You can search, filter, and favorite these link opportunities and track their statuses as you submit them.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Search - narrower */}
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search opportunities..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <SafeIcon
              icon={FiSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4"
            />
          </div>
        </div>

        {/* State Filter */}
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            State
          </label>
          <div className="relative">
            <select
              value={filters.state}
              onChange={(e) => handleFilterChange('state', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent appearance-none pr-10 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All States</option>
              {uniqueStates && uniqueStates.map(state => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <SafeIcon icon={FiIcons.FiChevronDown} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        {/* Niche Filter */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Niche
          </label>
          <div className="relative">
            <select
              value={filters.niche}
              onChange={(e) => handleFilterChange('niche', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent appearance-none pr-10 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Niches</option>
              {uniqueNiches.map(niche => (
                <option key={niche} value={niche}>
                  {niche}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <SafeIcon icon={FiIcons.FiChevronDown} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        {/* Type Filter */}
        <div className="md:col-span-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Type
          </label>
          <div className="relative">
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent appearance-none pr-10 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <SafeIcon icon={FiIcons.FiChevronDown} className="w-4 h-4 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        {/* Favorites Filter */}
        <div className="md:col-span-1 flex items-center md:items-center">
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Favorites
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="favoritesOnly"
                checked={filters.favorites || false}
                onChange={(e) => handleFilterChange('favorites', e.target.checked)}
                className="w-4 h-4 text-[#D59A4C] border-gray-300 rounded focus:ring-[#D59A4C] dark:border-gray-600 dark:bg-gray-700"
              />
              <label
                htmlFor="favoritesOnly"
                className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center"
              >
                <SafeIcon icon={FiStar} className="w-4 h-4 text-yellow-400 mr-1" />
                Only
              </label>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default LinkDirectoryFilters
